import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, ReactNode } from 'react';
import {
  Project,
  User,
  SubElement,
  TaskStatus,
  TaskFileType,
  TaskComment,
  TaskFile,
  TimeEntry,
  ProjectAnalytics,
  StatusHistoryEntry,
  ProjectAlert,
  AlertType,
  Priority,
  Zone,
  Section,
  Element,
  QuickTaskInput,
} from '../types';
import type {
  ZoneRow,
  SectionRow,
  ElementRow,
  SubElementRow,
  TaskFileRow,
  TaskAssigneeRow,
  TimeEntryRow,
  TaskCommentRow,
  StatusHistoryRow,
  ProjectAlertRow,
} from '../types/db';
import {
  getAllSubElements,
  calculateActualHours,
  calculateProjectProgress,
  calculateProjectWeightedProgress,
  setUsersCache,
  MOCK_PROJECTS,
} from '../data/mockData';
import { generateTempId } from '../utils/format';
import { createLogger } from '../utils/logger';
import { createMockTaskFile, uploadTaskFile, withRetry } from '../lib/storageHelpers';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';
import { notifyAppError } from '../utils/appEvents';
import { STORAGE_KEYS } from '../constants';

const logger = createLogger('ProjectContext');

interface CreateProjectAlertInput {
  type: AlertType;
  projectId: string;
  subElementId: string;
  taskCode: string;
  taskName: string;
  assignedTo: string[];
  message: string;
  createdAt?: string;
  endDate?: string;
  completedAt?: string;
}

function mapProjectAlertRow(row: ProjectAlertRow): ProjectAlert {
  return {
    id: row.id,
    type: row.type as AlertType,
    projectId: row.project_id,
    subElementId: row.sub_element_id,
    taskCode: row.task_code,
    taskName: row.task_name,
    assignedTo: row.assigned_to || [],
    message: row.message,
    createdAt: row.created_at,
    isRead: row.is_read,
    endDate: row.end_date || undefined,
    completedAt: row.completed_at || undefined,
  };
}

interface ProjectContextType {
  projects: Project[];
  activeProject: Project | null;
  setActiveProjectId: (id: string) => void;
  getProjectAnalytics: (project: Project) => ProjectAnalytics;
  updateTaskStatus: (subElementId: string, newStatus: TaskStatus, comment?: string, userId?: string, photoFiles?: File[]) => Promise<{ success: boolean; error?: string }>;
  addTaskComment: (subElementId: string, comment: Omit<TaskComment, 'id' | 'createdAt'>) => Promise<void>;
  updateTaskComment: (subElementId: string, commentId: string, content: string) => Promise<{ success: boolean; error?: string }>;
  deleteTaskComment: (subElementId: string, commentId: string) => Promise<{ success: boolean; error?: string }>;
  addTaskAttachmentFromFiles: (subElementId: string, files: File[], fileType: TaskFile['fileType'], uploadedBy: string) => Promise<TaskFile[]>;
  removeTaskAttachment: (subElementId: string, attachmentId: string) => void;
  startTimeEntry: (subElementId: string, userId: string) => void;
  stopTimeEntry: (subElementId: string, userId: string, notes: string) => void;
  addProject: (project: Omit<Project, 'id' | 'zones' | 'createdAt' | 'updatedAt'>) => void;
  updateProject: (projectId: string, updates: Partial<Project>) => void;
  deleteProject: (projectId: string) => void;
  addZone: (projectId: string, zone: Omit<Zone, 'id' | 'sections' | 'createdAt' | 'updatedAt'>) => void;
  updateZone: (zoneId: string, updates: Partial<Zone>) => void;
  deleteZone: (zoneId: string) => void;
  addSection: (zoneId: string, section: Omit<Section, 'id' | 'elements'>) => void;
  updateSection: (sectionId: string, updates: Partial<Section>) => void;
  deleteSection: (sectionId: string) => void;
  addElement: (sectionId: string, element: Omit<Element, 'id' | 'subElements'>) => void;
  updateElement: (elementId: string, updates: Partial<Element>) => void;
  deleteElement: (elementId: string) => void;
  addSubElement: (elementId: string, subElement: Omit<SubElement, 'id' | 'timeEntries' | 'comments' | 'attachments' | 'statusHistory' | 'createdAt' | 'updatedAt'>) => void;
  // Quick task: creates a task directly, auto-creating (or reusing) "General"
  // Zone > Section > Element containers so the user needn't build the tree first.
  createQuickTask: (projectId: string, task: QuickTaskInput) => Promise<{ success: boolean; error?: string }>;
  updateSubElementFields: (subElementId: string, updates: Partial<Pick<SubElement, 'name' | 'code' | 'description' | 'priority' | 'estimatedHours' | 'assignedTo' | 'startDate' | 'endDate'>>) => void;
  deleteSubElement: (subElementId: string) => void;
  // Marca una tarea como trabajo pedido por el cliente fuera del alcance original.
  markAsExtraWork: (subElementId: string, reason: string, authorizedBy: string, originTaskId?: string) => Promise<{ success: boolean; error?: string }>;
  // Reubica una tarea en otro punto del árbol (p. ej. sacarla de "General").
  moveSubElement: (subElementId: string, targetElementId: string) => Promise<{ success: boolean; error?: string }>;
  duplicateProject: (project: Project) => void;
  duplicateZone: (zone: Zone) => void;
  duplicateSection: (section: Section) => void;
  duplicateElement: (element: Element) => void;
  duplicateSubElement: (subElement: SubElement) => void;
  // Alerts
  alerts: ProjectAlert[];
  markAlertRead: (alertId: string) => void;
  markAllAlertsRead: () => void;
  unreadAlertCount: number;
  allUsers: User[];
  // Project members
  activeProjectMembers: User[];
  addProjectMember: (projectId: string, userId: string) => void;
  removeProjectMember: (projectId: string, userId: string) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { users: allUsers, currentUser, isProjectManager } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  // Restore the last selected project so a reload/login lands on it directly.
  const [activeProjectId, setActiveProjectIdState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.ACTIVE_PROJECT) || ''
  );
  const [alerts, setAlerts] = useState<ProjectAlert[]>([]);

  // Ref to always read latest projects without deps in callbacks
  const projectsRef = useRef<Project[]>([]);
  useEffect(() => { projectsRef.current = projects; }, [projects]);

  // Workers only see projects they are members of; PMs see all
  const visibleProjects = useMemo(() => {
    if (isProjectManager || !currentUser) return projects;
    return projects.filter(p => p.teamMembers.includes(currentUser.id));
  }, [projects, isProjectManager, currentUser]);

  const activeProject = visibleProjects.find(p => p.id === activeProjectId) || null;

  const setActiveProjectId = useCallback((id: string) => {
    setActiveProjectIdState(id);
  }, []);

  // Auto-select a project so the dashboard is never blank on login/reload.
  // Runs once projects are loaded: keeps the persisted selection if it's still
  // visible, otherwise falls back to the first visible project.
  useEffect(() => {
    if (visibleProjects.length === 0) return;
    const stillVisible = activeProjectId && visibleProjects.some(p => p.id === activeProjectId);
    if (!stillVisible) {
      setActiveProjectIdState(visibleProjects[0].id);
    }
  }, [visibleProjects, activeProjectId]);

  // Persist the active project across sessions.
  useEffect(() => {
    if (activeProjectId) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PROJECT, activeProjectId);
    }
  }, [activeProjectId]);

  // ==================== SUPABASE DATA FETCH ====================
  const fetchData = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      // Active project is chosen by the auto-select effect (respects persistence).
      setProjects(MOCK_PROJECTS);
      return;
    }
    const [projResult, membersResult] = await Promise.all([
      supabase.from('projects').select('*').order('created_at', { ascending: false }),
      supabase.from('project_members').select('project_id, user_id'),
    ]);
    if (projResult.error) { logger.error('fetch projects', projResult.error); return; }
    if (membersResult.error) { logger.error('fetch project_members', membersResult.error); return; }

    const membersMap = new Map<string, string[]>();
    (membersResult.data || []).forEach(pm => {
      const list = membersMap.get(pm.project_id) || [];
      list.push(pm.user_id);
      membersMap.set(pm.project_id, list);
    });

    const loaded: Project[] = (projResult.data || []).map(p => ({
      id: p.id,
      name: p.name,
      code: p.code,
      description: p.description || '',
      client: p.client || '',
      status: p.status as Project['status'],
      startDate: p.start_date || '',
      endDate: p.end_date || '',
      zones: [],
      teamMembers: membersMap.get(p.id) || [],
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    }));

    setProjects(prev => {
      // Preserve existing zones (hierarchy) when refreshing project metadata
      const zonesMap = new Map(prev.map(p => [p.id, p.zones]));
      return loaded.map(p => ({ ...p, zones: zonesMap.get(p.id) || p.zones }));
    });
  }, []);

  useEffect(() => {
    fetchData();

    if (!isSupabaseConfigured()) return;

    const projectsSub = supabase
      .channel('projects-channel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_members' }, () => fetchData())
      .subscribe();

    return () => {
      supabase.removeChannel(projectsSub);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== HIERARCHY LOADING ====================

  // Seed getUserName cache whenever real users load
  useEffect(() => {
    if (allUsers.length > 0) setUsersCache(allUsers);
  }, [allUsers]);

  const fetchProjectHierarchy = useCallback(async (projectId: string) => {
    if (!isSupabaseConfigured() || !projectId) return;
    logger.debug(`[FETCH] fetchProjectHierarchy called for project=${projectId}`);
    const { data, error } = await supabase
      .from('zones')
      .select(`
        *,
        sections(*,
          elements(*,
            sub_elements(*,
              task_assignees(user_id),
              time_entries(*),
              task_comments(*),
              status_history(*),
              task_files(*)
            )
          )
        )
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });
    if (error) { logger.error('[FETCH] fetchProjectHierarchy ERROR', error); return; }
    logger.debug(`[FETCH] Raw zones data received: ${(data || []).length} zones`);

    const zones: Zone[] = ((data || []) as ZoneRow[]).map((z): Zone => ({
      id: z.id, name: z.name, code: z.code,
      description: z.description || '', projectId: z.project_id,
      createdAt: z.created_at, updatedAt: z.updated_at,
      sections: (z.sections || []).map((s: SectionRow): Section => ({
        id: s.id, name: s.name, code: s.code,
        description: s.description || '', zoneId: s.zone_id,
        elements: (s.elements || []).map((e: ElementRow): Element => ({
          id: e.id, name: e.name, code: e.code,
          description: e.description || '', sectionId: e.section_id,
          subElements: (e.sub_elements || []).map((se: SubElementRow): SubElement => {
            const attachments: TaskFile[] = (se.task_files || []).map((f: TaskFileRow) => ({
              id: f.id, subElementId: se.id, commentId: f.comment_id || undefined,
              fileType: f.file_type as TaskFileType, fileName: f.file_name,
              storagePath: f.storage_path || '', mimeType: f.mime_type || '',
              fileSize: f.file_size || 0, uploadedBy: f.uploaded_by,
              createdAt: f.created_at,
              publicUrl: f.storage_path
                ? supabase.storage.from('task-files').getPublicUrl(f.storage_path).data.publicUrl
                : undefined,
            }));
            return {
              id: se.id, name: se.name, code: se.code,
              description: se.description || '', elementId: se.element_id,
              status: se.status as TaskStatus, priority: se.priority as Priority,
              estimatedHours: Number(se.estimated_hours) || 0,
              startDate: se.start_date || '', endDate: se.end_date || '',
              assignedTo: (se.task_assignees || []).map((a: TaskAssigneeRow) => a.user_id),
              timeEntries: (se.time_entries || []).map((te: TimeEntryRow): TimeEntry => ({
                id: te.id, subElementId: se.id, userId: te.user_id,
                startTime: te.start_time, endTime: te.end_time || null,
                isPaused: te.is_paused || false, pausedDuration: te.paused_duration || 0,
                notes: te.notes || '',
              })),
              // Un comentario borrado desaparece del hilo para todos.
              comments: (se.task_comments || [])
                .filter((c: TaskCommentRow) => !c.deleted_at)
                .map((c: TaskCommentRow): TaskComment => ({
                  id: c.id, subElementId: se.id, userId: c.user_id, userName: c.user_name || '',
                  content: c.content, createdAt: c.created_at,
                  attachments: attachments.filter(a => a.commentId === c.id),
                  editedAt: c.edited_at || undefined,
                })),
              statusHistory: (se.status_history || []).map((h: StatusHistoryRow): StatusHistoryEntry => ({
                id: h.id, subElementId: se.id, field: h.field,
                oldValue: h.old_value || '', newValue: h.new_value,
                timestamp: h.created_at, changedBy: h.changed_by,
                changedByName: h.changed_by_name, comment: h.comment || undefined,
                attachmentIds: h.attachment_ids || [],
              })),
              attachments,
              createdAt: se.created_at, updatedAt: se.updated_at,
              isExtraWork: se.is_extra_work || false,
              extraWorkReason: se.extra_work_reason || undefined,
              extraWorkBy: se.extra_work_by || undefined,
              extraWorkAt: se.extra_work_at || undefined,
              originTaskId: se.origin_task_id || undefined,
            };
          }),
        })),
      })),
    }));

    // LOG: Detailed summary of what we loaded
    let totalSE = 0, totalComments = 0, totalFiles = 0, totalHistory = 0, totalTimers = 0;
    zones.forEach(z => z.sections.forEach(s => s.elements.forEach(e => e.subElements.forEach(se => {
      totalSE++;
      totalComments += se.comments.length;
      totalFiles += se.attachments.length;
      totalHistory += se.statusHistory.length;
      totalTimers += se.timeEntries.length;
      if (se.comments.length > 0 || se.attachments.length > 0) {
        logger.debug(`[FETCH] SubElement ${se.code} (${se.id.slice(0,8)}): ${se.comments.length} comments, ${se.attachments.length} files, ${se.statusHistory.length} history`);
        se.comments.forEach(c => {
          logger.debug(`[FETCH]   Comment ${c.id.slice(0,8)}: "${c.content?.slice(0,50)}" attachments=${c.attachments.length}`, c.attachments.map(a => ({ id: a.id.slice(0,8), fileName: a.fileName, commentId: a.commentId?.slice(0,8), publicUrl: a.publicUrl?.slice(0,60) })));
        });
        se.attachments.forEach(a => {
          logger.debug(`[FETCH]   File ${a.id.slice(0,8)}: ${a.fileName} type=${a.fileType} commentId=${a.commentId?.slice(0,8) || 'none'} publicUrl=${a.publicUrl?.slice(0,60) || 'NONE'}`);
        });
      }
    }))));
    logger.debug(`[FETCH] Hierarchy loaded: ${zones.length} zones, ${totalSE} tasks, ${totalComments} comments, ${totalFiles} files, ${totalHistory} history, ${totalTimers} timers`);

    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, zones } : p));
    logger.debug(`[FETCH] setProjects updated for project=${projectId}`);
  }, []);

  const fetchAlerts = useCallback(async (projectId: string) => {
    if (!isSupabaseConfigured() || !projectId) return;
    const { data, error } = await supabase
      .from('project_alerts')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (error) { logger.error('fetchAlerts', error); return; }
    setAlerts(((data || []) as ProjectAlertRow[]).map(mapProjectAlertRow));
  }, []);

  const createProjectAlert = useCallback(async (input: CreateProjectAlertInput): Promise<ProjectAlert | null> => {
    const fallbackAlert: ProjectAlert = {
      id: generateTempId('alert'),
      type: input.type,
      projectId: input.projectId,
      subElementId: input.subElementId,
      taskCode: input.taskCode,
      taskName: input.taskName,
      assignedTo: input.assignedTo,
      message: input.message,
      createdAt: input.createdAt || new Date().toISOString(),
      isRead: false,
      endDate: input.endDate,
      completedAt: input.completedAt,
    };

    if (!isSupabaseConfigured()) {
      setAlerts(prev => {
        const exists = prev.some(a =>
          a.projectId === input.projectId &&
          a.subElementId === input.subElementId &&
          a.type === input.type &&
          a.message === input.message
        );
        return exists ? prev : [fallbackAlert, ...prev];
      });
      return fallbackAlert;
    }

    const { data, error } = await supabase.rpc('create_project_alert', {
      p_type: input.type,
      p_project_id: input.projectId,
      p_sub_element_id: input.subElementId,
      p_task_code: input.taskCode,
      p_task_name: input.taskName,
      p_assigned_to: input.assignedTo,
      p_message: input.message,
      p_end_date: input.endDate || null,
      p_completed_at: input.completedAt || null,
    });

    if (error) {
      logger.error('[ALERT] create_project_alert failed', error);
      return null;
    }

    const mapped = mapProjectAlertRow(data as ProjectAlertRow);
    setAlerts(prev => prev.some(a => a.id === mapped.id) ? prev : [mapped, ...prev]);
    return mapped;
  }, []);

  // Generate TASK_OVERDUE and TASK_DUE_SOON alerts from current project state
  const generateTimingAlerts = useCallback((projectId: string) => {
    const proj = projectsRef.current.find(p => p.id === projectId);
    if (!proj) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const timingAlerts: ProjectAlert[] = [];
    getAllSubElements(proj).forEach(task => {
      if (!task.endDate || task.status === 'FINISHED') return;
      const end = new Date(task.endDate + 'T23:59:59');
      const daysLeft = Math.ceil((end.getTime() - today.getTime()) / 86400000);
      if (daysLeft < 0) {
        timingAlerts.push({
          id: `overdue-${task.id}`,
          type: 'TASK_OVERDUE',
          projectId,
          subElementId: task.id,
          taskCode: task.code,
          taskName: task.name,
          assignedTo: task.assignedTo,
          message: `Task "${task.code}" is overdue by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''}.`,
          createdAt: task.endDate + 'T23:59:59.000Z',
          isRead: false,
          endDate: task.endDate,
        });
      } else if (daysLeft <= 3) {
        timingAlerts.push({
          id: `due-soon-${task.id}`,
          type: 'TASK_DUE_SOON',
          projectId,
          subElementId: task.id,
          taskCode: task.code,
          taskName: task.name,
          assignedTo: task.assignedTo,
          message: `Task "${task.code}" is due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}.`,
          createdAt: new Date().toISOString(),
          isRead: false,
          endDate: task.endDate,
        });
      }
    });
    if (timingAlerts.length > 0) {
      timingAlerts.forEach(alert => {
        void createProjectAlert({
          type: alert.type,
          projectId: alert.projectId,
          subElementId: alert.subElementId,
          taskCode: alert.taskCode,
          taskName: alert.taskName,
          assignedTo: alert.assignedTo,
          message: alert.message,
          createdAt: alert.createdAt,
          endDate: alert.endDate,
          completedAt: alert.completedAt,
        });
      });
    }
  }, [createProjectAlert]); // eslint-disable-line react-hooks/exhaustive-deps

  // PM: pre-load hierarchy for all projects so cross-project notifications work
  useEffect(() => {
    if (!isProjectManager || !isSupabaseConfigured()) return;
    const unloaded = projectsRef.current.filter(p => p.id !== activeProjectId && p.zones.length === 0);
    unloaded.forEach(p => fetchProjectHierarchy(p.id));
  }, [projects.length, isProjectManager, activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load hierarchy + alerts whenever active project changes
  useEffect(() => {
    if (activeProjectId) {
      fetchProjectHierarchy(activeProjectId);
      fetchAlerts(activeProjectId);
    }

    // Realtime for task-level changes so all team members see updates live
    if (!isSupabaseConfigured() || !activeProjectId) return;
    const reload = (payload?: { table?: string; eventType?: string; new?: { id?: string } }) => {
      logger.debug(`[REALTIME] Change detected, triggering fetchProjectHierarchy`, payload ? { table: payload.table, eventType: payload.eventType, newId: payload.new?.id?.slice(0,8) } : 'no payload');
      fetchProjectHierarchy(activeProjectId);
    };
    const taskSub = supabase
      .channel(`tasks-${activeProjectId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'zones' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sections' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'elements' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sub_elements' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignees' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'time_entries' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'status_history' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_files' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_alerts' }, () => fetchAlerts(activeProjectId))
      .subscribe();

    return () => { supabase.removeChannel(taskSub); };
  }, [activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate timing alerts for ALL visible projects once their hierarchy is loaded
  useEffect(() => {
    const loadedProjects = projectsRef.current.filter(p => p.zones.length > 0);
    if (loadedProjects.length === 0) return;
    loadedProjects.forEach(p => generateTimingAlerts(p.id));
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSubElement = useCallback((subElementId: string, updater: (se: SubElement) => SubElement) => {
    setProjects(prev => prev.map(project => ({
      ...project,
      zones: project.zones.map(zone => ({
        ...zone,
        sections: zone.sections.map(section => ({
          ...section,
          elements: section.elements.map(element => ({
            ...element,
            subElements: element.subElements.map(se =>
              se.id === subElementId ? updater(se) : se
            ),
          })),
        })),
      })),
    })));
  }, []);

  const updateTaskStatus = useCallback(async (subElementId: string, newStatus: TaskStatus, comment?: string, userId?: string, photoFiles?: File[]): Promise<{ success: boolean; error?: string }> => {
    logger.debug(`[STATUS] updateTaskStatus called: task=${subElementId.slice(0,8)} newStatus=${newStatus} comment="${comment?.slice(0,30) || ''}" photoFiles=${photoFiles?.length || 0} userId=${userId?.slice(0,8)}`);

    // Read current state for late-completion check
    const activeProj = projectsRef.current.find(p => p.id === activeProjectId);
    const currentSe = getAllSubElements(activeProj || {} as Project).find(se => se.id === subElementId);
    const oldStatus   = currentSe?.status   || 'ASSIGNED';
    const endDate     = currentSe?.endDate   || '';
    const assignedTo  = currentSe?.assignedTo || [];
    const taskCode    = currentSe?.code      || '';
    const taskName    = currentSe?.name      || '';
    const isLate = newStatus === 'FINISHED' && endDate !== '' && new Date() > new Date(endDate + 'T23:59:59');
    const isIssue = newStatus === 'ISSUES';

    if (isSupabaseConfigured() && userId) {
      // Optimistic status flip for instant feedback; history/attachments come
      // from the realtime reload. If the server rejects, we roll this back.
      updateSubElement(subElementId, se => ({ ...se, status: newStatus, updatedAt: new Date().toISOString() }));
      try {
        let uploadedFiles: TaskFile[] = [];
        if (photoFiles && photoFiles.length > 0) {
          logger.debug(`[STATUS] uploading ${photoFiles.length} photo files`);
          const results = await Promise.allSettled(photoFiles.map(f => uploadTaskFile(f, subElementId, 'status_photo', userId)));
          uploadedFiles = results.filter((r): r is PromiseFulfilledResult<TaskFile> => r.status === 'fulfilled').map(r => r.value);
          const failed = results.length - uploadedFiles.length;
          if (failed > 0) {
            updateSubElement(subElementId, se => ({ ...se, status: oldStatus }));
            return { success: false, error: `Failed to upload ${failed} photo(s). Status not changed.` };
          }
        }

        logger.debug(`[STATUS] calling transition_task_status RPC`);
        const r = await supabase.rpc('transition_task_status', {
          p_sub_element_id: subElementId,
          p_new_status: newStatus,
          p_comment: comment || null,
          p_attachment_ids: uploadedFiles.map(f => f.id),
        });
        if (r.error) throw r.error;

        logger.debug(`[STATUS] status change committed on server`);
        return { success: true };
      } catch (err: any) {
        // Roll the optimistic flip back so the UI matches the server.
        logger.error('[STATUS] updateTaskStatus ERROR — reverting optimistic status', err);
        updateSubElement(subElementId, se => ({ ...se, status: oldStatus }));
        return { success: false, error: err?.message || 'Could not change the task status.' };
      }
    } else {
      // Mock / offline path – full optimistic update
      const historyEntry: StatusHistoryEntry = {
        id: generateTempId('sh'), field: 'status',
        oldValue: oldStatus, newValue: newStatus,
        timestamp: new Date().toISOString(),
        changedBy: userId || 'unknown',
        changedByName: userId ? (allUsers.find(u => u.id === userId)?.name || 'Unknown') : 'Unknown',
        comment: comment || undefined,
      };

      if (isLate) {
        void createProjectAlert({
          type: 'LATE_COMPLETION',
          projectId: activeProjectId,
          subElementId, taskCode, taskName, assignedTo,
          message: `Task "${taskCode} - ${taskName}" was completed after its due date (${endDate}).`,
          createdAt: new Date().toISOString(),
          endDate,
          completedAt: new Date().toISOString(),
        });
      }

      if (isIssue) {
        void createProjectAlert({
          type: 'ISSUE_REPORTED',
          projectId: activeProjectId,
          subElementId, taskCode, taskName, assignedTo,
          message: `Task "${taskCode} - ${taskName}" has reported an issue.`,
          createdAt: new Date().toISOString(),
        });
      }

      if (photoFiles && photoFiles.length > 0 && userId) {
        Promise.all(photoFiles.map(f => createMockTaskFile(f, subElementId, 'status_photo', userId))).then(taskFiles => {
          updateSubElement(subElementId, se => ({
            ...se, status: newStatus,
            attachments: [...se.attachments, ...taskFiles],
            statusHistory: [...se.statusHistory, { ...historyEntry, attachmentIds: taskFiles.map(tf => tf.id) }],
            updatedAt: new Date().toISOString(),
          }));
        });
      } else {
        updateSubElement(subElementId, se => ({
          ...se, status: newStatus,
          statusHistory: [...se.statusHistory, historyEntry],
          updatedAt: new Date().toISOString(),
        }));
      }
      return { success: true };
    }
  }, [updateSubElement, activeProjectId, allUsers, createProjectAlert]);

  const updateTaskComment = useCallback(async (
    subElementId: string,
    commentId: string,
    content: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const trimmed = content.trim();
    if (!trimmed) return { success: false, error: 'The comment cannot be empty.' };

    if (isSupabaseConfigured()) {
      const { error } = await supabase
        .from('task_comments')
        .update({ content: trimmed })
        .eq('id', commentId);
      if (error) {
        logger.error('updateTaskComment', error);
        return { success: false, error: error.message };
      }
    }

    const editedAt = new Date().toISOString();
    updateSubElement(subElementId, se => ({
      ...se,
      comments: se.comments.map(c => c.id === commentId ? { ...c, content: trimmed, editedAt } : c),
      updatedAt: editedAt,
    }));

    return { success: true };
  }, [updateSubElement]);

  const deleteTaskComment = useCallback(async (
    subElementId: string,
    commentId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (isSupabaseConfigured()) {
      // Se marca como borrado en vez de eliminar la fila: la app ya no lo
      // muestra a nadie, y queda margen para restaurarlo si fue un error.
      const { error } = await supabase
        .from('task_comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', commentId);
      if (error) {
        logger.error('deleteTaskComment', error);
        return { success: false, error: error.message };
      }
    }

    updateSubElement(subElementId, se => ({
      ...se,
      comments: se.comments.filter(c => c.id !== commentId),
      updatedAt: new Date().toISOString(),
    }));

    return { success: true };
  }, [updateSubElement]);

  const addTaskComment = useCallback(async (subElementId: string, comment: Omit<TaskComment, 'id' | 'createdAt'>) => {
    logger.debug(`[COMMENT] addTaskComment called: task=${subElementId.slice(0,8)} content="${comment.content?.slice(0,40)}" attachments=${comment.attachments.length}`, comment.attachments.map(a => ({ id: a.id.slice(0,8), fileName: a.fileName })));
    let commentId = generateTempId('comment');
    let createdAt = new Date().toISOString();

    if (isSupabaseConfigured()) {
      logger.debug(`[COMMENT] Inserting comment into DB...`);
      const { data, error } = await withRetry(async () => {
        const r = await supabase.from('task_comments').insert({
          sub_element_id: subElementId, user_id: comment.userId,
          user_name: comment.userName, content: comment.content,
        }).select().single();
        if (r.error) throw r.error;
        return r;
      }).catch((err) => { logger.error('[COMMENT] addTaskComment DB INSERT ERROR', err); return { data: null, error: err }; });
      if (!error && data) {
        commentId = data.id;
        createdAt = data.created_at;
        logger.debug(`[COMMENT] Comment inserted: id=${data.id.slice(0,8)} createdAt=${createdAt}`);
        if (comment.attachments.length > 0) {
          logger.debug(`[COMMENT] Linking ${comment.attachments.length} attachments to comment ${data.id.slice(0,8)}`, comment.attachments.map(a => a.id.slice(0,8)));
          const { error: linkErr } = await supabase.from('task_files').update({ comment_id: data.id }).in('id', comment.attachments.map(a => a.id));
          if (linkErr) { logger.error('[COMMENT] link comment attachments ERROR', linkErr); }
          else { logger.debug(`[COMMENT] Attachments linked successfully`); }
        }
      }
    }
    // Always do optimistic update for instant feedback.
    // When Supabase realtime fires, fetchProjectHierarchy replaces state with server truth,
    // so the temp comment is naturally replaced — no dedup needed.
    logger.debug(`[COMMENT] Applying optimistic update (commentId=${commentId.slice(0,8)})`);
    const newCommentObj: TaskComment = { ...comment, id: commentId, createdAt };
    const attachmentIds = new Set(comment.attachments.map(a => a.id));
    updateSubElement(subElementId, se => ({
      ...se,
      comments: [...se.comments, newCommentObj],
      attachments: se.attachments.map(a => attachmentIds.has(a.id) ? { ...a, commentId } : a),
      updatedAt: new Date().toISOString(),
    }));

    // Generate NEW_COMMENT alert for assignees (except the commenter)
    const activeProj = projectsRef.current.find(p => p.id === activeProjectId);
    const currentTask = getAllSubElements(activeProj || {} as Project).find(se => se.id === subElementId);
    if (currentTask) {
      const recipients = currentTask.assignedTo.filter(id => id !== comment.userId);
      if (recipients.length > 0) {
        if (isSupabaseConfigured()) {
          void createProjectAlert({
            type: 'NEW_COMMENT',
            projectId: activeProjectId,
            subElementId,
            taskCode: currentTask.code,
            taskName: currentTask.name,
            assignedTo: recipients,
            message: `New comment on task "${currentTask.code}" from ${comment.userName}.`,
            createdAt,
          });
          return;
        }
        const commentAlert: ProjectAlert = {
          id: generateTempId('alert'),
          type: 'NEW_COMMENT',
          projectId: activeProjectId,
          subElementId,
          taskCode: currentTask.code,
          taskName: currentTask.name,
          assignedTo: recipients,
          message: `${comment.userName} commented on task "${currentTask.code}": "${comment.content.slice(0, 60)}${comment.content.length > 60 ? '…' : ''}"`,
          createdAt,
          isRead: false,
        };
        setAlerts(prev => [commentAlert, ...prev]);
      }
    }
  }, [updateSubElement, activeProjectId, createProjectAlert]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTaskAttachmentFromFiles = useCallback(async (
    subElementId: string,
    files: File[],
    fileType: TaskFile['fileType'],
    uploadedBy: string,
  ): Promise<TaskFile[]> => {
    logger.debug(`[ATTACH] addTaskAttachmentFromFiles: task=${subElementId.slice(0,8)} files=${files.length} type=${fileType}`);
    let taskFiles: TaskFile[];
    if (isSupabaseConfigured()) {
      const results = await Promise.allSettled(files.map(f => uploadTaskFile(f, subElementId, fileType, uploadedBy)));
      taskFiles = results.filter((r): r is PromiseFulfilledResult<TaskFile> => r.status === 'fulfilled').map(r => r.value);
      const rejected = results.filter(r => r.status === 'rejected');
      logger.debug(`[ATTACH] Upload results: ${taskFiles.length} success, ${rejected.length} failed`, taskFiles.map(f => ({ id: f.id.slice(0,8), fileName: f.fileName, publicUrl: f.publicUrl?.slice(0,60) })));
      if (rejected.length > 0) {
        const reasons = rejected.map(r => (r as PromiseRejectedResult).reason?.message || 'Upload failed').join('; ');
        logger.error('[ATTACH] Failed uploads', reasons);
        throw new Error(`${rejected.length} archivo(s) fallaron al subir: ${reasons}`);
      }
    } else {
      taskFiles = await Promise.all(files.map(f => createMockTaskFile(f, subElementId, fileType, uploadedBy)));
    }
    // When Supabase is active, skip optimistic update – realtime reload handles it.
    logger.debug(`[ATTACH] isSupabaseConfigured=${isSupabaseConfigured()}, will ${isSupabaseConfigured() ? 'SKIP' : 'DO'} optimistic update`);
    if (!isSupabaseConfigured()) {
      updateSubElement(subElementId, se => ({ ...se, attachments: [...se.attachments, ...taskFiles], updatedAt: new Date().toISOString() }));
    }
    return taskFiles;
  }, [updateSubElement]);

  const removeTaskAttachment = useCallback(async (subElementId: string, attachmentId: string) => {
    if (isSupabaseConfigured()) {
      // Find storagePath from local state
      let storagePath = '';
      for (const p of projectsRef.current) {
        for (const z of p.zones) for (const s of z.sections) for (const e of s.elements) {
          const se = e.subElements.find(se => se.id === subElementId);
          if (se) { const att = se.attachments.find(a => a.id === attachmentId); if (att) storagePath = att.storagePath; }
        }
      }
      await supabase.from('task_files').delete().eq('id', attachmentId);
      if (storagePath && !storagePath.startsWith('mock/')) {
        const { error: storageErr } = await supabase.storage.from('task-files').remove([storagePath]);
        if (storageErr) { logger.error('remove file from storage', storageErr); }
      }
    }
    updateSubElement(subElementId, se => ({ ...se, attachments: se.attachments.filter(a => a.id !== attachmentId), updatedAt: new Date().toISOString() }));
  }, [updateSubElement]);

  const startTimeEntry = useCallback(async (subElementId: string, userId: string) => {
    logger.debug(`[TIMER] startTimeEntry: task=${subElementId.slice(0,8)} user=${userId.slice(0,8)}`);
    const activeProj = projectsRef.current.find(p => p.id === activeProjectId);
    const se = getAllSubElements(activeProj || {} as Project).find(s => s.id === subElementId);

    // Skip if this user already has an open timer for this task
    if (se?.timeEntries.some(te => te.userId === userId && te.endTime === null)) {
      logger.debug(`[TIMER] SKIP: user ${userId.slice(0,8)} already has open timer`);
      return;
    }

    const shouldUpdateStatus = se?.status === 'ASSIGNED';
    let entryId = generateTempId('te');
    const startTime = new Date().toISOString();

    if (isSupabaseConfigured()) {
      const teResult = await supabase
        .from('time_entries')
        .insert({ sub_element_id: subElementId, user_id: userId, start_time: startTime })
        .select()
        .single();
      if (!teResult.error && teResult.data) entryId = teResult.data.id;
      if (shouldUpdateStatus) {
        const { error } = await supabase.rpc('transition_task_status', {
          p_sub_element_id: subElementId,
          p_new_status: 'STARTED',
          p_comment: 'Timer started.',
          p_attachment_ids: [],
        });
        if (error) logger.error('[TIMER] transition_task_status error', error);
      }
    }

    if (isSupabaseConfigured()) {
      // Only update status optimistically; timeEntry comes from realtime to avoid duplicates.
      if (shouldUpdateStatus) {
        updateSubElement(subElementId, se => ({ ...se, status: 'STARTED', updatedAt: new Date().toISOString() }));
      }
    } else {
      const entry: TimeEntry = { id: entryId, subElementId, userId, startTime, endTime: null, isPaused: false, pausedDuration: 0, notes: '' };
      updateSubElement(subElementId, se => ({
        ...se, timeEntries: [...se.timeEntries, entry],
        status: shouldUpdateStatus ? 'STARTED' : se.status,
        updatedAt: new Date().toISOString(),
      }));
    }
  }, [updateSubElement, activeProjectId]);

  const stopTimeEntry = useCallback(async (subElementId: string, userId: string, notes: string) => {
    logger.debug(`[TIMER] stopTimeEntry: task=${subElementId.slice(0,8)} user=${userId.slice(0,8)}`);
    const activeProj = projectsRef.current.find(p => p.id === activeProjectId);
    const se = getAllSubElements(activeProj || {} as Project).find(s => s.id === subElementId);
    const openEntry = se?.timeEntries.find(te => te.userId === userId && te.endTime === null);

    // Skip if this user has no open timer
    if (!openEntry) {
      logger.debug(`[TIMER] SKIP: user ${userId.slice(0,8)} has no open timer`);
      return;
    }
    logger.debug(`[TIMER] Stopping timer entry=${openEntry.id.slice(0,8)}`);

    if (isSupabaseConfigured()) {
      await withRetry(async () => {
        const r = await supabase.from('time_entries').update({ end_time: new Date().toISOString(), notes }).eq('id', openEntry.id);
        if (r.error) throw r.error;
      }).catch(err => logger.error('[TIMER] stopTimeEntry DB error', err));
      // Skip optimistic update – realtime reload handles it to avoid duplicates.
    } else {
      updateSubElement(subElementId, se => ({
        ...se,
        timeEntries: se.timeEntries.map(te => te.userId === userId && te.endTime === null ? { ...te, endTime: new Date().toISOString(), notes } : te),
        updatedAt: new Date().toISOString(),
      }));
    }
  }, [updateSubElement, activeProjectId]);

  // ==================== PROJECT CRUD ====================
  const addProject = useCallback(async (project: Omit<Project, 'id' | 'zones' | 'createdAt' | 'updatedAt'>) => {
    if (!isSupabaseConfigured()) {
      const newProject: Project = { ...project, id: generateTempId('proj'), zones: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      setProjects(prev => [...prev, newProject]);
      return;
    }
    const { data, error } = await supabase
      .from('projects')
      .insert({
        name: project.name,
        code: project.code,
        description: project.description || '',
        client: project.client || '',
        status: project.status,
        start_date: project.startDate || null,
        end_date: project.endDate || null,
      })
      .select()
      .single();
    if (error) { logger.error('addProject', error); notifyAppError(error.message); return; }
    const newProject: Project = {
      id: data.id,
      name: data.name,
      code: data.code,
      description: data.description || '',
      client: data.client || '',
      status: data.status,
      startDate: data.start_date || '',
      endDate: data.end_date || '',
      zones: [],
      teamMembers: [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
    setProjects(prev => [...prev, newProject]);
    setActiveProjectIdState(prev => prev || newProject.id);
  }, []);

  const updateProject = useCallback(async (projectId: string, updates: Partial<Project>) => {
    if (!isSupabaseConfigured()) {
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p));
      return;
    }
    const { error } = await supabase
      .from('projects')
      .update({
        name: updates.name,
        code: updates.code,
        description: updates.description,
        client: updates.client,
        status: updates.status,
        start_date: updates.startDate || null,
        end_date: updates.endDate || null,
      })
      .eq('id', projectId);
    if (error) { logger.error('updateProject', error); notifyAppError(error.message); return; }
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p));
  }, []);

  const deleteProject = useCallback(async (projectId: string) => {
    if (!isSupabaseConfigured()) {
      setProjects(prev => prev.filter(p => p.id !== projectId));
      return;
    }
    const { error } = await supabase.from('projects').delete().eq('id', projectId);
    if (error) { logger.error('deleteProject', error); notifyAppError(error.message); return; }
    setProjects(prev => {
      const remaining = prev.filter(p => p.id !== projectId);
      if (activeProjectId === projectId) {
        setActiveProjectIdState(remaining[0]?.id || '');
      }
      return remaining;
    });
  }, [activeProjectId]);

  // ==================== ZONE CRUD ====================
  const addZone = useCallback(async (projectId: string, zone: Omit<Zone, 'id' | 'sections' | 'createdAt' | 'updatedAt'>) => {
    if (!isSupabaseConfigured()) {
      const newZone: Zone = { ...zone, id: generateTempId('zone'), sections: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, zones: [...p.zones, newZone] } : p));
      return;
    }
    const { data, error } = await supabase.from('zones')
      .insert({ project_id: projectId, name: zone.name, code: zone.code, description: zone.description || '' })
      .select().single();
    if (error) { logger.error('addZone', error); notifyAppError(error.message); return; }
    const newZone: Zone = { id: data.id, name: data.name, code: data.code, description: data.description || '', projectId, sections: [], createdAt: data.created_at, updatedAt: data.updated_at };
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, zones: [...p.zones, newZone] } : p));
  }, []);

  const updateZone = useCallback(async (zoneId: string, updates: Partial<Zone>) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('zones').update({ name: updates.name, code: updates.code, description: updates.description }).eq('id', zoneId);
      if (error) { logger.error('updateZone', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => z.id === zoneId ? { ...z, ...updates, updatedAt: new Date().toISOString() } : z) })));
  }, []);

  const deleteZone = useCallback(async (zoneId: string) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('zones').delete().eq('id', zoneId);
      if (error) { logger.error('deleteZone', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.filter(z => z.id !== zoneId) })));
  }, []);

  // ==================== SECTION CRUD ====================
  const addSection = useCallback(async (zoneId: string, section: Omit<Section, 'id' | 'elements'>) => {
    if (!isSupabaseConfigured()) {
      const newSection: Section = { ...section, id: generateTempId('sec'), elements: [] };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => z.id === zoneId ? { ...z, sections: [...z.sections, newSection] } : z) })));
      return;
    }
    const { data, error } = await supabase.from('sections')
      .insert({ zone_id: zoneId, name: section.name, code: section.code, description: section.description || '' })
      .select().single();
    if (error) { logger.error('addSection', error); notifyAppError(error.message); return; }
    const newSection: Section = { id: data.id, name: data.name, code: data.code, description: data.description || '', zoneId, elements: [] };
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => z.id === zoneId ? { ...z, sections: [...z.sections, newSection] } : z) })));
  }, []);

  const updateSection = useCallback(async (sectionId: string, updates: Partial<Section>) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('sections').update({ name: updates.name, code: updates.code, description: updates.description }).eq('id', sectionId);
      if (error) { logger.error('updateSection', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => s.id === sectionId ? { ...s, ...updates } : s) })) })));
  }, []);

  const deleteSection = useCallback(async (sectionId: string) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('sections').delete().eq('id', sectionId);
      if (error) { logger.error('deleteSection', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.filter(s => s.id !== sectionId) })) })));
  }, []);

  // ==================== ELEMENT CRUD ====================
  const addElement = useCallback(async (sectionId: string, element: Omit<Element, 'id' | 'subElements'>) => {
    if (!isSupabaseConfigured()) {
      const newElement: Element = { ...element, id: generateTempId('el'), subElements: [] };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => s.id === sectionId ? { ...s, elements: [...s.elements, newElement] } : s) })) })));
      return;
    }
    const { data, error } = await supabase.from('elements')
      .insert({ section_id: sectionId, name: element.name, code: element.code, description: element.description || '' })
      .select().single();
    if (error) { logger.error('addElement', error); notifyAppError(error.message); return; }
    const newElement: Element = { id: data.id, name: data.name, code: data.code, description: data.description || '', sectionId, subElements: [] };
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => s.id === sectionId ? { ...s, elements: [...s.elements, newElement] } : s) })) })));
  }, []);

  const updateElement = useCallback(async (elementId: string, updates: Partial<Element>) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('elements').update({ name: updates.name, code: updates.code, description: updates.description }).eq('id', elementId);
      if (error) { logger.error('updateElement', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.map(e => e.id === elementId ? { ...e, ...updates } : e) })) })) })));
  }, []);

  const deleteElement = useCallback(async (elementId: string) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('elements').delete().eq('id', elementId);
      if (error) { logger.error('deleteElement', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.filter(e => e.id !== elementId) })) })) })));
  }, []);

  // ==================== SUBELEMENT CRUD ====================
  const addSubElement = useCallback(async (elementId: string, subElement: Omit<SubElement, 'id' | 'timeEntries' | 'comments' | 'attachments' | 'statusHistory' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    if (!isSupabaseConfigured()) {
      const newSe: SubElement = { ...subElement, id: generateTempId('se'), timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.map(e => e.id === elementId ? { ...e, subElements: [...e.subElements, newSe] } : e) })) })) })));
      return;
    }
    const { data, error } = await supabase.from('sub_elements').insert({
      element_id: elementId, name: subElement.name, code: subElement.code,
      description: subElement.description || '', status: subElement.status || 'ASSIGNED',
      priority: subElement.priority || 'MEDIUM', estimated_hours: subElement.estimatedHours || 0,
      start_date: subElement.startDate || null, end_date: subElement.endDate || null,
    }).select().single();
    if (error) { logger.error('addSubElement', error); notifyAppError(error.message); return; }
    if (subElement.assignedTo.length > 0) {
      await supabase.from('task_assignees').insert(subElement.assignedTo.map(uid => ({ sub_element_id: data.id, user_id: uid })));
    }
    const newSe: SubElement = {
      id: data.id, name: data.name, code: data.code, description: data.description || '',
      elementId, status: data.status, priority: data.priority,
      estimatedHours: Number(data.estimated_hours) || 0,
      startDate: data.start_date || '', endDate: data.end_date || '',
      assignedTo: subElement.assignedTo, timeEntries: [], comments: [], attachments: [], statusHistory: [],
      createdAt: data.created_at, updatedAt: data.updated_at,
    };
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.map(e => e.id === elementId ? { ...e, subElements: [...e.subElements, newSe] } : e) })) })) })));
  }, []);

  const createQuickTask = useCallback(async (projectId: string, task: QuickTaskInput): Promise<{ success: boolean; error?: string }> => {
    const GENERAL = 'General';
    const proj = projectsRef.current.find(p => p.id === projectId);
    if (!proj) return { success: false, error: 'Project not found.' };

    // Los campos opcionales son un anexo: omitirlos da el mismo resultado
    // que antes de que existieran.
    const description = task.description || '';
    const assignedTo = task.assignedTo || [];
    const estimatedHours = task.estimatedHours || 0;
    const startDate = task.startDate || '';
    const endDate = task.endDate || '';

    // Mock path: build the chain in memory, reusing "General" containers.
    if (!isSupabaseConfigured()) {
      let zone = task.elementId ? undefined : proj.zones.find(z => z.name === GENERAL);
      let section = zone?.sections.find(s => s.name === GENERAL);
      let element = section?.elements.find(e => e.name === GENERAL);
      const now = new Date().toISOString();
      const newTask: SubElement = {
        id: generateTempId('se'), name: task.name, code: task.code, description,
        elementId: task.elementId || element?.id || generateTempId('el'),
        status: 'ASSIGNED', priority: task.priority || 'MEDIUM',
        assignedTo, estimatedHours, startDate, endDate,
        timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
      };

      // Destino explícito en el árbol: insertar ahí y salir.
      if (task.elementId) {
        setProjects(prev => prev.map(p => p.id !== projectId ? p : {
          ...p,
          zones: p.zones.map(z => ({
            ...z,
            sections: z.sections.map(s => ({
              ...s,
              elements: s.elements.map(e => e.id === task.elementId
                ? { ...e, subElements: [...e.subElements, newTask] }
                : e),
            })),
          })),
        }));
        return { success: true };
      }
      element = element || { id: newTask.elementId, name: GENERAL, code: GENERAL, description: '', sectionId: section?.id || generateTempId('sec'), subElements: [] };
      section = section || { id: element.sectionId, name: GENERAL, code: GENERAL, description: '', zoneId: zone?.id || generateTempId('zone'), elements: [] };
      zone = zone || { id: section.zoneId, name: GENERAL, code: GENERAL, description: '', projectId, sections: [], createdAt: now, updatedAt: now };
      setProjects(prev => prev.map(p => {
        if (p.id !== projectId) return p;
        const zones = [...p.zones];
        let z = zones.find(x => x.id === zone!.id); if (!z) { z = { ...zone!, sections: [] }; zones.push(z); }
        let s = z.sections.find(x => x.id === section!.id); if (!s) { s = { ...section!, elements: [] }; z.sections = [...z.sections, s]; }
        let e = s.elements.find(x => x.id === element!.id); if (!e) { e = { ...element!, subElements: [] }; s.elements = [...s.elements, e]; }
        e.subElements = [...e.subElements, newTask];
        return { ...p, zones };
      }));
      return { success: true };
    }

    // Live path: find-or-create each "General" container in the DB, then the task.
    try {
      const findOrCreate = async (table: string, parentCol: string, parentId: string, existingId?: string): Promise<string> => {
        if (existingId) return existingId;
        const { data, error } = await supabase.from(table)
          .insert({ [parentCol]: parentId, name: GENERAL, code: GENERAL, description: 'Auto-created for quick tasks' })
          .select('id').single();
        if (error) throw error;
        return data.id;
      };
      let elementId = task.elementId;
      if (!elementId) {
        const zone = proj.zones.find(z => z.name === GENERAL);
        const zoneId = await findOrCreate('zones', 'project_id', projectId, zone?.id);
        const section = zone?.sections.find(s => s.name === GENERAL);
        const sectionId = await findOrCreate('sections', 'zone_id', zoneId, section?.id);
        const element = section?.elements.find(e => e.name === GENERAL);
        elementId = await findOrCreate('elements', 'section_id', sectionId, element?.id);
      }

      const { data: created, error: taskErr } = await supabase.from('sub_elements').insert({
        element_id: elementId, name: task.name, code: task.code, description,
        status: 'ASSIGNED', priority: task.priority || 'MEDIUM',
        estimated_hours: estimatedHours,
        start_date: startDate || null,
        end_date: endDate || null,
      }).select('id').single();
      if (taskErr) throw taskErr;

      if (assignedTo.length > 0 && created) {
        const { error: assignErr } = await supabase.from('task_assignees')
          .insert(assignedTo.map(uid => ({ sub_element_id: created.id, user_id: uid })));
        if (assignErr) logger.error('createQuickTask assignees', assignErr);
      }

      await fetchProjectHierarchy(projectId);
      return { success: true };
    } catch (err: any) {
      logger.error('createQuickTask', err);
      return { success: false, error: err?.message || 'Could not create the task.' };
    }
  }, [fetchProjectHierarchy]);

  const updateSubElementFields = useCallback(async (subElementId: string, updates: Partial<Pick<SubElement, 'name' | 'code' | 'description' | 'priority' | 'estimatedHours' | 'assignedTo' | 'startDate' | 'endDate'>>) => {
    if (isSupabaseConfigured()) {
      const dbUp: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUp.name = updates.name;
      if (updates.code !== undefined) dbUp.code = updates.code;
      if (updates.description !== undefined) dbUp.description = updates.description;
      if (updates.priority !== undefined) dbUp.priority = updates.priority;
      if (updates.estimatedHours !== undefined) dbUp.estimated_hours = updates.estimatedHours;
      if (updates.startDate !== undefined) dbUp.start_date = updates.startDate || null;
      if (updates.endDate !== undefined) dbUp.end_date = updates.endDate || null;
      if (Object.keys(dbUp).length > 0) {
        const { error } = await supabase.from('sub_elements').update(dbUp).eq('id', subElementId);
        if (error) { logger.error('updateSubElementFields', error); }
      }
      if (updates.assignedTo !== undefined) {
        const { error: delErr } = await supabase.from('task_assignees').delete().eq('sub_element_id', subElementId);
        if (delErr) { logger.error('delete task_assignees', delErr); }
        if (updates.assignedTo.length > 0) {
          const { error: insErr } = await supabase.from('task_assignees').insert(updates.assignedTo.map(uid => ({ sub_element_id: subElementId, user_id: uid })));
          if (insErr) { logger.error('insert task_assignees', insErr); notifyAppError('Error saving assigned personnel: ' + insErr.message); }
        }
      }
    }
    // Generate TASK_ASSIGNED alert for newly added assignees
    if (updates.assignedTo !== undefined) {
      const activeProj = projectsRef.current.find(p => p.id === activeProjectId);
      const currentTask = getAllSubElements(activeProj || {} as Project).find(se => se.id === subElementId);
      const prevAssigned = currentTask?.assignedTo ?? [];
      const newAssignees = updates.assignedTo.filter(id => !prevAssigned.includes(id));
      if (newAssignees.length > 0 && currentTask) {
        const now = new Date().toISOString();
        newAssignees.forEach(uid => {
          void createProjectAlert({
            type: 'TASK_ASSIGNED',
            projectId: activeProjectId,
            subElementId,
            taskCode: currentTask.code,
            taskName: currentTask.name,
            assignedTo: [uid],
            message: `Task "${currentTask.code} - ${currentTask.name}" has been assigned to you.`,
            createdAt: now,
          });
        });
      }
    }
    updateSubElement(subElementId, se => ({ ...se, ...updates, updatedAt: new Date().toISOString() }));
  }, [updateSubElement, activeProjectId, createProjectAlert]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveSubElement = useCallback(async (
    subElementId: string,
    targetElementId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase
        .from('sub_elements')
        .update({ element_id: targetElementId })
        .eq('id', subElementId);
      if (error) {
        logger.error('moveSubElement', error);
        return { success: false, error: error.message };
      }
      await fetchProjectHierarchy(activeProjectId);
      return { success: true };
    }

    setProjects(prev => prev.map(p => {
      let moved: SubElement | undefined;
      const stripped = p.zones.map(z => ({
        ...z,
        sections: z.sections.map(s => ({
          ...s,
          elements: s.elements.map(e => {
            const found = e.subElements.find(se => se.id === subElementId);
            if (!found) return e;
            moved = found;
            return { ...e, subElements: e.subElements.filter(se => se.id !== subElementId) };
          }),
        })),
      }));
      if (!moved) return p;
      return {
        ...p,
        zones: stripped.map(z => ({
          ...z,
          sections: z.sections.map(s => ({
            ...s,
            elements: s.elements.map(e => e.id === targetElementId
              ? { ...e, subElements: [...e.subElements, { ...moved!, elementId: targetElementId }] }
              : e),
          })),
        })),
      };
    }));
    return { success: true };
  }, [fetchProjectHierarchy, activeProjectId]);

  const markAsExtraWork = useCallback(async (
    subElementId: string,
    reason: string,
    authorizedBy: string,
    originTaskId?: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const now = new Date().toISOString();

    if (isSupabaseConfigured()) {
      const { error } = await supabase
        .from('sub_elements')
        .update({
          is_extra_work: true,
          extra_work_reason: reason,
          extra_work_by: authorizedBy,
          extra_work_at: now,
          origin_task_id: originTaskId ?? null,
        })
        .eq('id', subElementId);

      if (error) {
        logger.error('markAsExtraWork', error);
        return { success: false, error: error.message };
      }
    }

    updateSubElement(subElementId, se => ({
      ...se,
      isExtraWork: true,
      extraWorkReason: reason,
      extraWorkBy: authorizedBy,
      extraWorkAt: now,
      originTaskId: originTaskId ?? se.originTaskId,
      updatedAt: now,
    }));

    return { success: true };
  }, [updateSubElement]);

  const deleteSubElement = useCallback(async (subElementId: string) => {
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('sub_elements').delete().eq('id', subElementId);
      if (error) { logger.error('deleteSubElement', error); return; }
    }
    setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.map(e => ({ ...e, subElements: e.subElements.filter(se => se.id !== subElementId) })) })) })) })));
  }, []);

  // ==================== DUPLICATE ====================
  const duplicateProject = useCallback(async (project: Project) => {
    const now = new Date().toISOString();
    if (!isSupabaseConfigured()) {
      const newProjId = generateTempId('proj');
      const cloned: Project = {
        ...project, id: newProjId, name: project.name + ' (Copy)', createdAt: now, updatedAt: now,
        teamMembers: [...project.teamMembers],
        zones: project.zones.map(zone => {
          const newZoneId = generateTempId('zone');
          return {
            ...zone, id: newZoneId, projectId: newProjId, createdAt: now, updatedAt: now,
            sections: zone.sections.map(sec => {
              const newSecId = generateTempId('sec');
              return {
                ...sec, id: newSecId, zoneId: newZoneId,
                elements: sec.elements.map(el => {
                  const newElId = generateTempId('el');
                  return {
                    ...el, id: newElId, sectionId: newSecId,
                    subElements: el.subElements.map(se => ({
                      ...se, id: generateTempId('se'), elementId: newElId,
                      timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
                    })),
                  };
                }),
              };
            }),
          };
        }),
      };
      setProjects(prev => [...prev, cloned]);
      return;
    }
    // Supabase path: create project first
    const { data: pData, error: pErr } = await supabase.from('projects')
      .insert({ name: project.name + ' (Copy)', code: project.code, description: project.description || '', client: project.client || '', status: project.status, start_date: project.startDate || null, end_date: project.endDate || null })
      .select().single();
    if (pErr) { logger.error('duplicateProject', pErr); notifyAppError(pErr.message); return; }
    // Copy team members
    if (project.teamMembers.length > 0) {
      await supabase.from('project_members').insert(project.teamMembers.map(uid => ({ project_id: pData.id, user_id: uid })));
    }
    const newProject: Project = {
      id: pData.id, name: pData.name, code: pData.code, description: pData.description || '',
      client: pData.client || '', status: pData.status,
      startDate: pData.start_date || '', endDate: pData.end_date || '',
      zones: [], teamMembers: [...project.teamMembers],
      createdAt: pData.created_at, updatedAt: pData.updated_at,
    };
    for (const zone of project.zones) {
      const { data: zData, error: zErr } = await supabase.from('zones')
        .insert({ project_id: pData.id, name: zone.name, code: zone.code, description: zone.description || '' })
        .select().single();
      if (zErr) { logger.error('duplicateProject:zone', zErr); continue; }
      const newZone: Zone = { id: zData.id, name: zData.name, code: zData.code, description: zData.description || '', projectId: pData.id, sections: [], createdAt: zData.created_at, updatedAt: zData.updated_at };
      for (const sec of zone.sections) {
        const { data: secData, error: secErr } = await supabase.from('sections')
          .insert({ zone_id: zData.id, name: sec.name, code: sec.code, description: sec.description || '' })
          .select().single();
        if (secErr) { logger.error('duplicateProject:section', secErr); continue; }
        const newSection: Section = { id: secData.id, name: secData.name, code: secData.code, description: secData.description || '', zoneId: zData.id, elements: [] };
        for (const el of sec.elements) {
          const { data: elData, error: elErr } = await supabase.from('elements')
            .insert({ section_id: secData.id, name: el.name, code: el.code, description: el.description || '' })
            .select().single();
          if (elErr) { logger.error('duplicateProject:element', elErr); continue; }
          const newElement: Element = { id: elData.id, name: elData.name, code: elData.code, description: elData.description || '', sectionId: secData.id, subElements: [] };
          for (const se of el.subElements) {
            const { data: seData, error: seErr } = await supabase.from('sub_elements').insert({
              element_id: elData.id, name: se.name, code: se.code, description: se.description || '',
              status: se.status || 'ASSIGNED', priority: se.priority || 'MEDIUM',
              estimated_hours: se.estimatedHours || 0, start_date: se.startDate || null, end_date: se.endDate || null,
            }).select().single();
            if (seErr) { logger.error('duplicateProject:subElement', seErr); continue; }
            if (se.assignedTo.length > 0) {
              await supabase.from('task_assignees').insert(se.assignedTo.map(uid => ({ sub_element_id: seData.id, user_id: uid })));
            }
            newElement.subElements.push({
              id: seData.id, name: seData.name, code: seData.code, description: seData.description || '',
              elementId: elData.id, status: seData.status, priority: seData.priority,
              estimatedHours: Number(seData.estimated_hours) || 0,
              startDate: seData.start_date || '', endDate: seData.end_date || '',
              assignedTo: se.assignedTo, timeEntries: [], comments: [], attachments: [], statusHistory: [],
              createdAt: seData.created_at, updatedAt: seData.updated_at,
            });
          }
          newSection.elements.push(newElement);
        }
        newZone.sections.push(newSection);
      }
      newProject.zones.push(newZone);
    }
    // Realtime subscription will add the project to state
  }, []);

  const duplicateZone = useCallback(async (zone: Zone) => {
    const now = new Date().toISOString();
    const projectId = zone.projectId;
    if (!isSupabaseConfigured()) {
      const newZoneId = generateTempId('zone');
      const cloned: Zone = {
        ...zone, id: newZoneId, name: zone.name + ' (Copy)', createdAt: now, updatedAt: now,
        sections: zone.sections.map(sec => {
          const newSecId = generateTempId('sec');
          return {
            ...sec, id: newSecId, zoneId: newZoneId,
            elements: sec.elements.map(el => {
              const newElId = generateTempId('el');
              return {
                ...el, id: newElId, sectionId: newSecId,
                subElements: el.subElements.map(se => ({
                  ...se, id: generateTempId('se'), elementId: newElId,
                  timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
                })),
              };
            }),
          };
        }),
      };
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, zones: [...p.zones, cloned] } : p));
      return;
    }
    const { data: zData, error: zErr } = await supabase.from('zones')
      .insert({ project_id: projectId, name: zone.name + ' (Copy)', code: zone.code, description: zone.description || '' })
      .select().single();
    if (zErr) { logger.error('duplicateZone', zErr); notifyAppError(zErr.message); return; }
    const newZone: Zone = { id: zData.id, name: zData.name, code: zData.code, description: zData.description || '', projectId, sections: [], createdAt: zData.created_at, updatedAt: zData.updated_at };
    for (const sec of zone.sections) {
      const { data: secData, error: secErr } = await supabase.from('sections')
        .insert({ zone_id: zData.id, name: sec.name, code: sec.code, description: sec.description || '' })
        .select().single();
      if (secErr) { logger.error('duplicateZone:section', secErr); continue; }
      const newSection: Section = { id: secData.id, name: secData.name, code: secData.code, description: secData.description || '', zoneId: zData.id, elements: [] };
      for (const el of sec.elements) {
        const { data: elData, error: elErr } = await supabase.from('elements')
          .insert({ section_id: secData.id, name: el.name, code: el.code, description: el.description || '' })
          .select().single();
        if (elErr) { logger.error('duplicateZone:element', elErr); continue; }
        const newElement: Element = { id: elData.id, name: elData.name, code: elData.code, description: elData.description || '', sectionId: secData.id, subElements: [] };
        for (const se of el.subElements) {
          const { data: seData, error: seErr } = await supabase.from('sub_elements').insert({
            element_id: elData.id, name: se.name, code: se.code, description: se.description || '',
            status: se.status || 'ASSIGNED', priority: se.priority || 'MEDIUM',
            estimated_hours: se.estimatedHours || 0, start_date: se.startDate || null, end_date: se.endDate || null,
          }).select().single();
          if (seErr) { logger.error('duplicateZone:subElement', seErr); continue; }
          if (se.assignedTo.length > 0) {
            await supabase.from('task_assignees').insert(se.assignedTo.map(uid => ({ sub_element_id: seData.id, user_id: uid })));
          }
          newElement.subElements.push({
            id: seData.id, name: seData.name, code: seData.code, description: seData.description || '',
            elementId: elData.id, status: seData.status, priority: seData.priority,
            estimatedHours: Number(seData.estimated_hours) || 0,
            startDate: seData.start_date || '', endDate: seData.end_date || '',
            assignedTo: se.assignedTo, timeEntries: [], comments: [], attachments: [], statusHistory: [],
            createdAt: seData.created_at, updatedAt: seData.updated_at,
          });
        }
        newSection.elements.push(newElement);
      }
      newZone.sections.push(newSection);
    }
    // Realtime subscription will update state
  }, []);

  const duplicateSubElement = useCallback(async (subElement: SubElement) => {
    const now = new Date().toISOString();
    const elementId = subElement.elementId;
    if (!isSupabaseConfigured()) {
      const cloned: SubElement = {
        ...subElement, id: generateTempId('se'), name: subElement.name + ' (Copy)',
        timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
      };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => ({ ...s, elements: s.elements.map(e => e.id === elementId ? { ...e, subElements: [...e.subElements, cloned] } : e) })) })) })));
      return;
    }
    const { data, error } = await supabase.from('sub_elements').insert({
      element_id: elementId, name: subElement.name + ' (Copy)', code: subElement.code,
      description: subElement.description || '', status: subElement.status || 'ASSIGNED',
      priority: subElement.priority || 'MEDIUM', estimated_hours: subElement.estimatedHours || 0,
      start_date: subElement.startDate || null, end_date: subElement.endDate || null,
    }).select().single();
    if (error) { logger.error('duplicateSubElement', error); notifyAppError(error.message); return; }
    if (subElement.assignedTo.length > 0) {
      await supabase.from('task_assignees').insert(subElement.assignedTo.map(uid => ({ sub_element_id: data.id, user_id: uid })));
    }
    // Realtime subscription will update state
  }, []);

  const duplicateElement = useCallback(async (element: Element) => {
    const now = new Date().toISOString();
    const sectionId = element.sectionId;
    if (!isSupabaseConfigured()) {
      const newElId = generateTempId('el');
      const cloned: Element = {
        ...element, id: newElId, name: element.name + ' (Copy)',
        subElements: element.subElements.map(se => ({
          ...se, id: generateTempId('se'), elementId: newElId,
          timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
        })),
      };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => ({ ...z, sections: z.sections.map(s => s.id === sectionId ? { ...s, elements: [...s.elements, cloned] } : s) })) })));
      return;
    }
    const { data: elData, error: elErr } = await supabase.from('elements')
      .insert({ section_id: sectionId, name: element.name + ' (Copy)', code: element.code, description: element.description || '' })
      .select().single();
    if (elErr) { logger.error('duplicateElement', elErr); notifyAppError(elErr.message); return; }
    const newElement: Element = { id: elData.id, name: elData.name, code: elData.code, description: elData.description || '', sectionId, subElements: [] };
    for (const se of element.subElements) {
      const { data: seData, error: seErr } = await supabase.from('sub_elements').insert({
        element_id: elData.id, name: se.name, code: se.code, description: se.description || '',
        status: se.status || 'ASSIGNED', priority: se.priority || 'MEDIUM',
        estimated_hours: se.estimatedHours || 0, start_date: se.startDate || null, end_date: se.endDate || null,
      }).select().single();
      if (seErr) { logger.error('duplicateElement:subElement', seErr); continue; }
      if (se.assignedTo.length > 0) {
        await supabase.from('task_assignees').insert(se.assignedTo.map(uid => ({ sub_element_id: seData.id, user_id: uid })));
      }
      newElement.subElements.push({
        id: seData.id, name: seData.name, code: seData.code, description: seData.description || '',
        elementId: elData.id, status: seData.status, priority: seData.priority,
        estimatedHours: Number(seData.estimated_hours) || 0,
        startDate: seData.start_date || '', endDate: seData.end_date || '',
        assignedTo: se.assignedTo, timeEntries: [], comments: [], attachments: [], statusHistory: [],
        createdAt: seData.created_at, updatedAt: seData.updated_at,
      });
    }
    // Realtime subscription will update state
  }, []);

  const duplicateSection = useCallback(async (section: Section) => {
    const now = new Date().toISOString();
    const zoneId = section.zoneId;
    if (!isSupabaseConfigured()) {
      const newSecId = generateTempId('sec');
      const cloned: Section = {
        ...section, id: newSecId, name: section.name + ' (Copy)',
        elements: section.elements.map(el => {
          const newElId = generateTempId('el');
          return {
            ...el, id: newElId, sectionId: newSecId,
            subElements: el.subElements.map(se => ({
              ...se, id: generateTempId('se'), elementId: newElId,
              timeEntries: [], comments: [], attachments: [], statusHistory: [], createdAt: now, updatedAt: now,
            })),
          };
        }),
      };
      setProjects(prev => prev.map(p => ({ ...p, zones: p.zones.map(z => z.id === zoneId ? { ...z, sections: [...z.sections, cloned] } : z) })));
      return;
    }
    const { data: secData, error: secErr } = await supabase.from('sections')
      .insert({ zone_id: zoneId, name: section.name + ' (Copy)', code: section.code, description: section.description || '' })
      .select().single();
    if (secErr) { logger.error('duplicateSection', secErr); notifyAppError(secErr.message); return; }
    const newSection: Section = { id: secData.id, name: secData.name, code: secData.code, description: secData.description || '', zoneId, elements: [] };
    for (const el of section.elements) {
      const { data: elData, error: elErr } = await supabase.from('elements')
        .insert({ section_id: secData.id, name: el.name, code: el.code, description: el.description || '' })
        .select().single();
      if (elErr) { logger.error('duplicateSection:element', elErr); continue; }
      const newElement: Element = { id: elData.id, name: elData.name, code: elData.code, description: elData.description || '', sectionId: secData.id, subElements: [] };
      for (const se of el.subElements) {
        const { data: seData, error: seErr } = await supabase.from('sub_elements').insert({
          element_id: elData.id, name: se.name, code: se.code, description: se.description || '',
          status: se.status || 'ASSIGNED', priority: se.priority || 'MEDIUM',
          estimated_hours: se.estimatedHours || 0, start_date: se.startDate || null, end_date: se.endDate || null,
        }).select().single();
        if (seErr) { logger.error('duplicateSection:subElement', seErr); continue; }
        if (se.assignedTo.length > 0) {
          await supabase.from('task_assignees').insert(se.assignedTo.map(uid => ({ sub_element_id: seData.id, user_id: uid })));
        }
        newElement.subElements.push({
          id: seData.id, name: seData.name, code: seData.code, description: seData.description || '',
          elementId: elData.id, status: seData.status, priority: seData.priority,
          estimatedHours: Number(seData.estimated_hours) || 0,
          startDate: seData.start_date || '', endDate: seData.end_date || '',
          assignedTo: se.assignedTo, timeEntries: [], comments: [], attachments: [], statusHistory: [],
          createdAt: seData.created_at, updatedAt: seData.updated_at,
        });
      }
      newSection.elements.push(newElement);
    }
    // Realtime subscription will update state
  }, []);

  // ==================== ALERTS ====================
  const markAlertRead = useCallback((alertId: string) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, isRead: true } : a));
    if (!isSupabaseConfigured()) return;
    (async () => {
      const { error } = await supabase.from('project_alerts').update({ is_read: true }).eq('id', alertId);
      if (error) logger.error('[ALERT] markAlertRead DB error', error);
    })();
  }, []);

  const markAllAlertsRead = useCallback(() => {
    const unreadIds = alerts.filter(a => !a.isRead).map(a => a.id);
    setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
    if (!isSupabaseConfigured() || unreadIds.length === 0) return;
    (async () => {
      const { error } = await supabase.from('project_alerts').update({ is_read: true }).in('id', unreadIds);
      if (error) logger.error('[ALERT] markAllAlertsRead DB error', error);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  const unreadAlertCount = useMemo(() => alerts.filter(a => !a.isRead).length, [alerts]);

  // ==================== PROJECT MEMBERS ====================
  const activeProjectMembers = useMemo(() => {
    if (!activeProject) return [];
    return allUsers.filter(u => activeProject.teamMembers.includes(u.id));
  }, [activeProject, allUsers]);

  const addProjectMember = useCallback(async (projectId: string, userId: string) => {
    if (!isSupabaseConfigured()) {
      setProjects(prev => prev.map(p =>
        p.id === projectId && !p.teamMembers.includes(userId)
          ? { ...p, teamMembers: [...p.teamMembers, userId], updatedAt: new Date().toISOString() }
          : p
      ));
      return;
    }
    const { error } = await supabase
      .from('project_members')
      .insert({ project_id: projectId, user_id: userId });
    if (error) { logger.error('addProjectMember', error); return; }
    setProjects(prev => prev.map(p =>
      p.id === projectId && !p.teamMembers.includes(userId)
        ? { ...p, teamMembers: [...p.teamMembers, userId] }
        : p
    ));
  }, []);

  const removeProjectMember = useCallback(async (projectId: string, userId: string) => {
    if (!isSupabaseConfigured()) {
      setProjects(prev => prev.map(p =>
        p.id === projectId
          ? { ...p, teamMembers: p.teamMembers.filter(id => id !== userId), updatedAt: new Date().toISOString() }
          : p
      ));
      return;
    }
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);
    if (error) { logger.error('removeProjectMember', error); return; }
    setProjects(prev => prev.map(p =>
      p.id === projectId
        ? { ...p, teamMembers: p.teamMembers.filter(id => id !== userId) }
        : p
    ));
  }, []);

  const getProjectAnalytics = useCallback((project: Project): ProjectAnalytics => {
    const allSubs = getAllSubElements(project);
    const total = allSubs.length;
    const completed = allSubs.filter(s => s.status === 'FINISHED').length;
    const inProgress = allSubs.filter(s => s.status === 'IN_PROGRESS').length;
    const issues = allSubs.filter(s => s.status === 'ISSUES').length;
    const assigned = allSubs.filter(s => s.status === 'ASSIGNED').length;
    const started = allSubs.filter(s => s.status === 'STARTED').length;

    const totalEstimated = allSubs.reduce((sum, s) => sum + s.estimatedHours, 0);
    const totalActual = allSubs.reduce((sum, s) => sum + calculateActualHours(s.timeEntries), 0);

    return {
      totalTasks: total,
      completedTasks: completed,
      inProgressTasks: inProgress,
      issueTasks: issues,
      assignedTasks: assigned,
      startedTasks: started,
      progressPercentage: calculateProjectProgress(project),
      weightedProgressPercentage: calculateProjectWeightedProgress(project),
      totalEstimatedHours: totalEstimated,
      totalActualHours: Math.round(totalActual * 100) / 100,
      hoursVariance: Math.round((totalActual - totalEstimated) * 100) / 100,
    };
  }, []);

  // Auto-select first visible project when none is active or active is not visible
  useEffect(() => {
    if (visibleProjects.length > 0 && !visibleProjects.find(p => p.id === activeProjectId)) {
      setActiveProjectIdState(visibleProjects[0].id);
    }
  }, [visibleProjects, activeProjectId]);

  const value: ProjectContextType = {
    projects: visibleProjects,
    activeProject,
    setActiveProjectId,
    getProjectAnalytics,
    updateTaskStatus,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    addTaskAttachmentFromFiles,
    removeTaskAttachment,
    startTimeEntry,
    stopTimeEntry,
    addProject,
    updateProject,
    deleteProject,
    addZone,
    updateZone,
    deleteZone,
    addSection,
    updateSection,
    deleteSection,
    addElement,
    updateElement,
    deleteElement,
    addSubElement,
    createQuickTask,
    updateSubElementFields,
    deleteSubElement,
    markAsExtraWork,
    moveSubElement,
    duplicateProject,
    duplicateZone,
    duplicateSection,
    duplicateElement,
    duplicateSubElement,
    alerts,
    markAlertRead,
    markAllAlertsRead,
    unreadAlertCount,
    allUsers,
    activeProjectMembers,
    addProjectMember,
    removeProjectMember,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjects = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};
